import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Layout from './layout';
import  PatientNavbar from './Navbar';
import DoctorNavbar from './DocNav';
import AdminDashboard from './AdminDashboard';
import axios from 'axios';
import { FaUser, FaEnvelope, FaPhone, FaIdCard, FaUserMd, FaBuilding } from 'react-icons/fa';
import AdminNavbar from './AdminNav';

const ProfilePage = () => {
    const { currentUser, updateUserProfile, logout } = useAuth();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        dateOfBirth: '',
        address: '',
        specialization: '',
        medicalLicense: '',
        gender: '',
        bloodType: '',
        allergies: ''
    });
    
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        if (currentUser) {
            setFormData({
                firstName: currentUser.firstName || '',
                lastName: currentUser.lastName || '',
                email: currentUser.email || '',
                phoneNumber: currentUser.phone || '',
                dateOfBirth: currentUser.dateOfBirth ? new Date(currentUser.dateOfBirth).toISOString().split('T')[0] : '',
                address: currentUser.address || '',
                specialization: currentUser.specialization || '',
                medicalLicense: currentUser.medicalLicense || '',
                gender: currentUser.gender || '',
                bloodType: currentUser.bloodType || '',
                allergies: currentUser.allergies || '',
                
            });
        }
    }, [currentUser]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/users/${currentUser._id}`;
            const response = await axios.put(apiUrl, formData, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.status === 200) {
                updateUserProfile(response.data);
                setSuccessMessage('Profile updated successfully!');
                setIsEditing(false);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (err) {
            setError('Failed to log out');
        }
    };

    return (
        <>
        {currentUser?.role === 'doctor' && <DoctorNavbar />}
        {currentUser?.role === 'patient' && <PatientNavbar />}
        {currentUser?.role === 'admin' && <AdminNavbar />}
            <div className="container mx-auto px-4 py-8">
                <div className="max-w-4xl mx-auto">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-3xl font-bold text-gray-800">My Profile</h1>
                        <div>
                            {!isEditing && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md mr-2"
                                >
                                    Edit Profile
                                </button>
                            )}
                            <button
                                onClick={handleLogout}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md"
                            >
                                Logout
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                            {error}
                        </div>
                    )}

                    {successMessage && (
                        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
                            {successMessage}
                        </div>
                    )}

                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="p-6">
                            <div className="flex items-center mb-6">
                                <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mr-6">
                                    <FaUser className="text-blue-500 text-4xl" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold">{currentUser?.firstName} {currentUser?.lastName}</h2>
                                    <p className="text-gray-600">{currentUser?.email}</p>
                                    <div className="mt-1 inline-block bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full">
                                        {currentUser?.role?.charAt(0).toUpperCase() + currentUser?.role?.slice(1)}
                                    </div>
                                </div>
                            </div>

                            <form onSubmit={handleUpdateProfile}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <FaUser className="inline mr-2" /> First Name
                                        </label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            value={formData.firstName}
                                            onChange={handleChange}
                                            disabled={!isEditing}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <FaUser className="inline mr-2" /> Last Name
                                        </label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            value={formData.lastName}
                                            onChange={handleChange}
                                            disabled={!isEditing}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <FaEnvelope className="inline mr-2" /> Email
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            value={formData.email}
                                            onChange={handleChange}
                                            disabled={!isEditing}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <FaPhone className="inline mr-2" /> Phone Number
                                        </label>
                                        <input
                                            type="text"
                                            name="phoneNumber"
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            value={formData.phoneNumber}
                                            onChange={handleChange}
                                            disabled={!isEditing}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Date of Birth
                                        </label>
                                        <input
                                            type="date"
                                            name="dateOfBirth"
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            value={formData.dateOfBirth}
                                            onChange={handleChange}
                                            disabled={!isEditing}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <FaBuilding className="inline mr-2" /> Address
                                        </label>
                                        <input
                                            type="text"
                                            name="address"
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            value={formData.address}
                                            onChange={handleChange}
                                            disabled={!isEditing}
                                        />
                                    </div>

                                    {currentUser?.role === 'doctor' && (
                                        <>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    <FaUserMd className="inline mr-2" /> Specialization
                                                </label>
                                                <input
                                                    type="text"
                                                    name="specialization"
                                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                    value={formData.specialization}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    <FaIdCard className="inline mr-2" /> Medical License
                                                </label>
                                                <input
                                                    type="text"
                                                    name="medicalLicense"
                                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                    value={formData.medicalLicense}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                />
                                            </div>
                                        </>
                                    )}

                                    {currentUser?.role === 'patient' && (
                                        <>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Gender
                                                </label>
                                                <select
                                                    name="gender"
                                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                    value={formData.gender}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                >
                                                    <option value="">Select Gender</option>
                                                    <option value="male">Male</option>
                                                    <option value="female">Female</option>
                                                    <option value="other">Other</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Blood Type
                                                </label>
                                                <select
                                                    name="bloodType"
                                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                    value={formData.bloodType}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                >
                                                    <option value="">Select Blood Type</option>
                                                    <option value="A+">A+</option>
                                                    <option value="A-">A-</option>
                                                    <option value="B+">B+</option>
                                                    <option value="B-">B-</option>
                                                    <option value="AB+">AB+</option>
                                                    <option value="AB-">AB-</option>
                                                    <option value="O+">O+</option>
                                                    <option value="O-">O-</option>
                                                </select>
                                            </div>

                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Allergies
                                                </label>
                                                <textarea
                                                    name="allergies"
                                                    rows="3"
                                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                                    value={formData.allergies}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                    placeholder="List any allergies here"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                                {isEditing && (
                                    <div className="mt-6 flex justify-end">
                                        <button
                                            type="button"
                                            className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md mr-2"
                                            onClick={() => setIsEditing(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
                                            disabled={loading}
                                        >
                                            {loading ? 'Updating...' : 'Save Changes'}
                                        </button>
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProfilePage;